import { SortAscending, SortDescending } from "@phosphor-icons/react"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useMemo } from "react"
import { formatISODate } from "../../../utils/datetime"
import Loader from "../../../components/Loader"
import Pagination from "../../../components/Pagination"
import Filter from "../../../components/TableFilters"

const columnHelper = createColumnHelper()

const IssuersTable = (props) => {
  const {
    data,
    loading,
    onPaginationChange,
    pagination,
    pageCount,
    sorting,
    setSorting,
    filters,
    setFilters,
    setEditedIssuerId,
    setDeleteIssuerId,
  } = props

  const columns = useMemo(
    () => [
      columnHelper.accessor("IssuerName", {
        header: "Patient Issuer Name",
        id: "IssuerName",
        size: 200,
        cell: (info) => info.renderValue() || "",
        sortDescFirst: false,
        meta: {
          filterVariant: "text",
        },
      }),
      columnHelper.accessor("isIssuerActive", {
        header: "Status",
        id: "isIssuerActive",
        size: 100,
        cell: (info) => (info.renderValue() ? "Active" : "In Active" || ""),
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("reportVersion", {
        header: "Report Version",
        id: "reportVersion",
        size: 150,
        cell: (info) => info.renderValue() || "",
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("createdBy", {
        header: "Created By",
        id: "createdBy",
        size: 150,
        cell: (info) => info.renderValue() || "",
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("lastModifiedBy", {
        header: "Last Modified By",
        id: "lastModifiedBy",
        size: 150,
        cell: (info) => info.renderValue() || "",
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("createdAt", {
        header: "Created On",
        size: 150,
        id: "createdAt",
        cell: (info) =>
          info.renderValue() ? formatISODate(info.renderValue()) : "",
        sortDescFirst: false,
        meta: {
          filterVariant: "select",
          options: [
            "All",
            "Today",
            "Yesterday",
            "This Week",
            "Last Week",
            "This Month",
            "Last Month",
          ],
        },
      }),
      columnHelper.accessor("PatientIssuerId", {
        header: "Actions",
        id: "PatientIssuerId",
        size: 150,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <button
              className="bg-secondary hover:bg-secondary_dark px-2 py-1 text-sm text-white rounded"
              onClick={() => setEditedIssuerId(row.original.PatientIssuerId)}
            >
              Edit
            </button>
            <button
              className="bg-red-700 hover:bg-red-800 text-white text-sm py-1 px-2 rounded"
              onClick={() => setDeleteIssuerId(row.original.PatientIssuerId)}
            >
              Delete
            </button>
          </div>
        ),
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
    ],
    [setDeleteIssuerId, setEditedIssuerId]
  )

  const table = useReactTable({
    columns,
    data,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange,
    onSortingChange: setSorting,
    onColumnFiltersChange: setFilters,
    pageCount,
    state: {
      pagination,
      sorting,
      columnFilters: filters,
    },
  })

  return (
    <>
      <div className="p-4">
        <table className="min-w-full bg-white border border-gray-200">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((column) => (
                  <th
                    key={column.id}
                    className="p-2 border-b border-gray-200 bg-gray-100 align-top whitespace"
                    style={{ width: `${column.getSize()}px` }}
                  >
                    <div className="flex flex-col justify-start items-start">
                      <button
                        {...{
                          className: column.column.getCanSort()
                            ? "w-full select-none cursor-pointer flex items-center gap-1"
                            : "w-full",
                          onClick: column.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          column.column.columnDef.header,
                          column.getContext()
                        )}
                        {{
                          asc: <SortAscending className="h-4 w-4" />,
                          desc: <SortDescending className="h-4 w-4" />,
                        }[column.column.getIsSorted()] ?? null}
                      </button>
                      {column.column.getCanFilter() ? (
                        <div className="mt-2">
                          <Filter column={column.column} />
                        </div>
                      ) : null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {loading ? (
            <tbody>
              <tr>
                <td colSpan={table.getAllColumns().length}>
                  <div className="py-2">
                    <Loader />
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-100">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-4 py-2 border-b border-gray-200"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>
        {!loading && data.length > 0 && <Pagination tableLib={table} />}
        {!loading && data.length === 0 && (
          <div className="mt-3 text-center">No patient issuers found</div>
        )}
      </div>
    </>
  )
}

export default IssuersTable
